import { KeywordChip } from "@/components/posting/parts";
import type { Application } from "@/lib/api/envoy";
import type { PostingFit } from "@/lib/api/resumeService";
import { useApplicationFit } from "@/lib/hooks/envoy";

/** The most chips one row shows before it says how many more there are. */
const MOST_CHIPS = 8;

/**
 * The keywords a fit counts, as chips under a posting: the ones the resume
 * says in green, then the ones it does not, outlined. Some of each when
 * both are there. Nothing when the fit has no keywords or its lists were
 * not saved.
 */
export default function FitKeywords({
    application,
    fit,
}: {
    application?: Application;
    fit?: PostingFit | null;
}) {
    const saved = useApplicationFit(
        application && application.keywords_asked > 0 ? application.application_id : null,
    );
    const covered = application ? saved.data?.covered : fit?.covered_keywords;
    const missing = application ? saved.data?.missing : fit?.missing.map((keyword) => keyword.term);
    if (!covered || !missing || covered.length + missing.length === 0) return null;

    const missingShown = missing.slice(
        0,
        Math.max(MOST_CHIPS - covered.length, Math.floor(MOST_CHIPS / 2)),
    );
    const coveredShown = covered.slice(0, MOST_CHIPS - missingShown.length);
    const more = covered.length + missing.length - coveredShown.length - missingShown.length;
    return (
        <ul
            aria-label={`${covered.length} keywords on your resume, ${missing.length} not`}
            className="m-0 flex list-none flex-wrap items-center gap-1 p-0"
        >
            {coveredShown.map((term, index) => (
                // A posting can ask for the same word twice.
                <li key={`covered:${index}:${term}`}>
                    <KeywordChip term={term} kind="covered" className={CHIP} />
                </li>
            ))}
            {missingShown.map((term, index) => (
                <li key={`missing:${index}:${term}`}>
                    <KeywordChip term={term} kind="missing" className={CHIP} />
                </li>
            ))}
            {more > 0 && (
                <li className="whitespace-nowrap text-[11px] text-fg-faint">+{more} more</li>
            )}
        </ul>
    );
}

const CHIP = "px-2 py-[2px] text-[11px] whitespace-nowrap";
